import React from 'react';
import { Box, VStack, Text, Button, Code } from '@chakra-ui/react';

// A deploy replaces the app's files, so a tab opened before it can ask for
// a chunk that no longer exists. Reload once to load the new version; if that
// already happened moments ago, show the error instead of reloading in a loop.
export const isChunkLoadError = error => error?.name === 'ChunkLoadError'
  || /Loading (CSS )?chunk [\w-]+ failed/i.test(String(error?.message || ''));
const RELOAD_KEY = 'pabs:chunk-reload-at';
function reloadOnceForNewVersion() {
  try {
    if (Date.now() - Number(sessionStorage.getItem(RELOAD_KEY) || 0) < 30000) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

// The app previously had no error boundary anywhere, so any render-time
// exception (e.g. a job detail panel hitting an unexpected data shape)
// unmounted the whole React tree and left the user with a blank white page.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, reloading: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    if (isChunkLoadError(error) && reloadOnceForNewVersion()) {
      this.setState({ reloading: true });
      return;
    }
    console.error('ErrorBoundary caught a render error:', error, info?.componentStack);
  }

  render() {
    if (this.state.reloading) {
      return <Box minHeight="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center" p={6}>
        <Text color="gray.600">Loading the latest version of Pabs…</Text>
      </Box>;
    }
    if (this.state.error) {
      return (
        <Box minHeight="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center" p={6}>
          <VStack spacing={4} maxW="480px" textAlign="center">
            <Text fontSize="xl" fontWeight="600" color="gray.800">Something went wrong</Text>
            <Text color="gray.600">
              Pabs hit an unexpected error rendering this page. Refreshing usually fixes it.
            </Text>
            <Code fontSize="xs" color="red.600" p={2} borderRadius="md" whiteSpace="pre-wrap">
              {this.state.error?.message}
            </Code>
            <Button colorScheme="blue" onClick={() => window.location.reload()}>Refresh page</Button>
          </VStack>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
